import { useEffect, useRef, useState, type ReactNode } from "react";

export function Mark() {
  return (
    <span className="brand-mark" aria-hidden>
      <svg width="16" height="16" viewBox="0 0 32 32">
        <path d="M7 9h18M16 9v16M10 17h4" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" fill="none" style={{ color: "var(--on-accent)" }} />
      </svg>
    </span>
  );
}

export function Choice<T extends string>({ options, value, onChange, label }: {
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="choice-group" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="choice" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="notice error" role="alert">{error instanceof Error ? error.message : String(error)}</div>;
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="empty" aria-busy>{label}</div>;
}

/** A button with a drop-down list of actions. */
export function Menu({ label, children, primary }: { label: ReactNode; children: ReactNode; primary?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="menu" ref={ref}>
      <button type="button" className={`btn${primary ? " primary" : ""}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
      </button>
      {open && (
        <div className="menu-panel" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

/** Opens the file picker and hands the chosen file over. */
export function useFilePicker(onFile: (file: File) => void) {
  return () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,image/jpeg,image/png,image/heic";
    input.onchange = () => input.files?.[0] && onFile(input.files[0]);
    input.click();
  };
}

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

/** A button that opens a small form; on save, passes the result back. */
export function InlineForm<T>({ title, fields, submit, onDone }: {
  title: string;
  fields: { id: string; label: string; type: string; hint?: string; optional?: boolean; options?: { value: string; label: string }[]; future?: boolean }[];
  submit: (values: Record<string, string>) => Promise<T>;
  onDone: (result: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (!open) return <button type="button" className="btn" style={{ alignSelf: "flex-start" }} onClick={() => setOpen(true)}>{title}</button>;
  return (
    <form className="card flat stack" style={{ "--gap": "12px" } as React.CSSProperties}
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
          onDone(await submit(values));
          setOpen(false);
          setValues({});
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}>
      <h3>{title}</h3>
      <div className="fields-2">
        {fields.map((f) => (
          <div className="field" key={f.id}>
            <label htmlFor={f.id}>{f.label}{f.optional && <span className="muted"> (optional)</span>}</label>
            {f.options ? (
              <select id={f.id} value={values[f.id] ?? ""} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} required={!f.optional}>
                <option value="">Choose…</option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input id={f.id} type={f.type} value={values[f.id] ?? ""} max={f.type === "date" && !f.future ? todayIso() : undefined}
                inputMode={f.type === "text" ? "decimal" : undefined} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} required={!f.optional} />
            )}
            {f.hint && <span className="hint">{f.hint}</span>}
          </div>
        ))}
      </div>
      <ErrorNotice error={error} />
      <div className="row"><div className="spacer" /><button type="button" className="btn ghost" onClick={() => setOpen(false)}>Cancel</button><button type="submit" className="btn primary" disabled={busy}>Save</button></div>
    </form>
  );
}
